import React, { useCallback, useState } from "react";

import {
  faFlask,
  faPlus,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { NavLink } from "react-router-dom";

import { useFlows } from "src/api/queries";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { useEntitySchemas } from "src/entities/queries";
import { FeatureCatalogueWrapper } from "src/featureCatalogue/FeatureCatalogue";
import { FeatureQueryMetrics } from "src/featureCatalogue/Features/FeatureQueryMetrics";
import { Features } from "src/featureCatalogue/Features/Features";
import { ManageFeatureModal } from "src/featureCatalogue/Features/ManageFeatureModal";
import {
  ViewByType,
  ViewTypeDropdown,
} from "src/featureCatalogue/Features/ViewTypeDropdown";
import { Queries } from "src/featureCatalogue/Queries/Queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useAllJobs } from "src/jobs/api/queries";
import { SubHeaderSearchBox } from "src/layout/SubHeader";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getUrlToManageFeatureQueryPage } from "src/router/urls";

const FeaturesHeader: React.FC<{
  onSearch: (query: string) => void;
  openCreateModal: () => void;
  currentViewOption: ViewByType;
  onCurrentViewChange: React.Dispatch<React.SetStateAction<ViewByType>>;
  searchKey: number;
}> = ({
  onSearch,
  openCreateModal,
  currentViewOption,
  onCurrentViewChange,
  searchKey,
}) => {
  const [environment, setEnvironment] = useEnvironment();
  const { featureCatalogue, featureQueriesCatalogue } = useCapabilities();
  const { orgId, workspace } = useWorkspaceContext();

  // Prefetch data used so it's available instantly during the confirmation modal
  useFlows({ includeSchemas: true, workspaceId: workspace.id });
  useAllJobs(workspace.base_url!, { statuses: ["active"] });
  useEntitySchemas({ baseUrl: workspace.base_url! });

  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  const dropdownItems: OptionsDropdownElement[] = [
    {
      key: "Create Feature",
      icon: faPlus,
      action: openCreateModal,
      disabled: !featureCatalogue.canCreate
        ? "You don't have permission to create features"
        : undefined,
    },
    MENU_DIVIDER,
    {
      key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
      icon: environment === "sandbox" ? faWavePulse : faFlask,
      action: () =>
        setEnvironment(environment === "sandbox" ? "live" : "sandbox"),
    },
  ];

  return (
    <>
      {featureLifecycleEnabled && (
        <ViewTypeDropdown
          selected={currentViewOption}
          onSelect={onCurrentViewChange}
        />
      )}
      <SubHeaderSearchBox
        key={searchKey}
        dataLoc="features-search"
        defaultValue=""
        placeholder="Search..."
        onChange={onSearch}
      />
      {featureLifecycleEnabled ? (
        featureQueriesCatalogue.canCreateQuery && (
          <div className="flex items-center border-l p-3.5">
            <NavLink
              data-loc="features-manage-link"
              to={getUrlToManageFeatureQueryPage(orgId, workspace.id)}
            >
              <Icon icon={faPlus} />
            </NavLink>
          </div>
        )
      ) : (
        <span className="border-gray-200 border-l p-3.5">
          <EllipsisOptionsDropdown
            dataLoc="features-menu-dropdown"
            elements={dropdownItems}
            iconSize="sm"
            placement="bottom start"
          />
        </span>
      )}
    </>
  );
};

export const FeaturesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchKey, setSearchKey] = useState(0);
  const { openModal, closeModal, isOpen, afterLeave } = useModal();
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const [currentViewOption, setCurrentViewOption] =
    useState<ViewByType>("Feature");

  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setSearchKey((k) => k + 1);
  }, []);

  useDocumentTitle(["Features"]);

  return (
    <FeatureCatalogueWrapper
      subHeaderRightContent={
        <FeaturesHeader
          currentViewOption={currentViewOption}
          openCreateModal={openModal}
          searchKey={searchKey}
          onCurrentViewChange={setCurrentViewOption}
          onSearch={setSearchQuery}
        />
      }
    >
      {featureLifecycleEnabled && <FeatureQueryMetrics />}
      <div
        className={`${featureLifecycleEnabled ? "flex min-h-0 w-full flex-1 flex-col" : ""}`}
      >
        {featureLifecycleEnabled && currentViewOption === "Query" ? (
          <Queries resetSearch={resetSearch} searchQuery={searchQuery} />
        ) : (
          <Features
            openCreateModal={openModal}
            resetSearch={resetSearch}
            searchQuery={searchQuery}
          />
        )}
      </div>
      <ManageFeatureModal
        afterLeave={afterLeave}
        isOpen={isOpen}
        mode="create"
        onClose={closeModal}
      />
    </FeatureCatalogueWrapper>
  );
};
